import type { FC, ReactNode } from "react";

import { HudActionButton } from "../common/HudActionButton";
import { type HudStatusTone, HudStatusPill } from "../common/HudStatusPill";
import { Text } from "../common/Text";
import ThemeToggle from "../common/ThemeToggle";

interface DashboardHeaderProps {
  kicker: string;
  title: string;
  subtitle: string;
  onRefresh: () => void;
  onLogout?: () => void;
  loading: boolean;
  showActions?: boolean;
  realtime?: { status: string; isHealthy: boolean };
  titleAdornment?: ReactNode;
}

interface RealtimePillSpec {
  label: string;
  tone: HudStatusTone;
  pulse: boolean;
}

function getRealtimePill(
  realtime: { status: string; isHealthy: boolean } | undefined,
): RealtimePillSpec | null {
  if (!realtime) {
    return null;
  }
  if (realtime.isHealthy) {
    return { label: "Live", pulse: true, tone: "success" };
  }
  if (realtime.status === "connecting" || realtime.status === "reconnecting") {
    return { label: "Connecting", pulse: true, tone: "warning" };
  }
  return { label: "Offline", pulse: false, tone: "danger" };
}

const RefreshIcon: FC = () => (
  <svg
    className="h-3 w-3"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

export const DashboardHeader: FC<DashboardHeaderProps> = ({
  kicker,
  title,
  subtitle,
  onRefresh,
  onLogout,
  loading,
  showActions = true,
  realtime,
  titleAdornment,
}) => {
  const realtimePill = getRealtimePill(realtime);

  return (
    <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
      <div className="flex-1">
        <p className="hidden items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 lg:flex dark:text-slate-500">
          <span aria-hidden className="text-slate-300 dark:text-slate-700">
            [
          </span>
          <span>{kicker}</span>
          <span aria-hidden className="text-slate-300 dark:text-slate-700">
            ]
          </span>
        </p>
        <div className="mt-1 flex items-center gap-3">
          <h1 className="font-mono text-lg font-bold uppercase tracking-[0.14em] text-slate-900 dark:text-white">
            {title}
          </h1>
          {titleAdornment}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <Text as="p">{subtitle}</Text>
          {realtimePill && (
            <HudStatusPill
              label={realtimePill.label}
              tone={realtimePill.tone}
              pulse={realtimePill.pulse}
            />
          )}
        </div>
      </div>
      {showActions && (
        <div className="flex flex-wrap items-center gap-2">
          <ThemeToggle />
          <HudActionButton
            label={loading ? "Refreshing" : "Refresh"}
            tone="neutral"
            loading={loading}
            leftIcon={<RefreshIcon />}
            onClick={onRefresh}
          />
          {onLogout && (
            <HudActionButton
              label="Sign Out"
              tone="neutral"
              onClick={onLogout}
            />
          )}
        </div>
      )}
    </header>
  );
};
